import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { 
  HiOutlinePrinter, HiOutlineChevronLeft, HiOutlineBolt, 
  HiOutlineUser, HiOutlineInformationCircle, HiOutlineShieldCheck,
  HiOutlinePhone, HiOutlineCurrencyRupee, HiOutlineCalendar,
  HiOutlineEnvelope, HiOutlineBuildingOffice, HiOutlineReceiptTax
} from "react-icons/hi2";
import { baseurl } from "../../baseurl";
import { BsReceipt } from "react-icons/bs";

const ElectricityInvoice = () => {
  const { ticketId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await axios.get(`${baseurl}/getBookingByReferenceUser/${ticketId}`);
        if (response.data.status === "success") {
          setBooking(response.data.booking);
        }
      } catch (error) {
        console.error("Error fetching electricity booking:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  }, [ticketId]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border text-danger" role="status"></div>
      <h4 className="ms-3">Fetching Receipt...</h4>
    </div>
  );

  if (!booking) return <div className="text-center py-5"><h4>Receipt not found.</h4></div>;

  // Shortcuts to nested data
  const request = booking.request_payload;
  const response = booking.response_payload;
  const consumerNo = request?.customerParams?.find(p => p.name === "Consumer No")?.value;

  return (
    <div className="invoice-wrapper py-4 px-2">
      <style>
        {`
          @media print {
            body { visibility: hidden; background: white !important; }
            .no-print { display: none !important; }
            #printable-invoice { 
              visibility: visible; 
              position: absolute; 
              left: 0; top: 0; width: 100%; 
              margin: 0; padding: 0;
              box-shadow: none !important;
              border: none !important;
            }
          }
          .bg-electricity { background-color: #ffc107 !important; } /* Electric Yellow */
          .text-electricity { color: #e67e22 !important; }
          .bg-dark-blue { background-color: #1e3a8a !important; }
          .invoice-card { border-radius: 15px; overflow: hidden; }
          .dashed-line { border-top: 2px dashed #dee2e6; margin: 20px 0; }
        `}
      </style>

      {/* Navigation */}
      <div className="container no-print mb-3  " style={{marginTop: "100px"}}>
        <div className="d-flex justify-content-between align-items-center" style={{ maxWidth: '850px', margin: '0 auto' }}>
          <button className="btn btn-light shadow-sm btn-sm" onClick={() => window.history.back()}>
            <HiOutlineChevronLeft /> Back
          </button>
          <button className="btn btn-dark shadow-sm btn-sm px-4" onClick={handlePrint}>
            <HiOutlinePrinter className="me-1" /> Print Receipt
          </button>
        </div>
      </div>

      <div className="invoice-card mx-auto shadow-lg bg-white" id="printable-invoice" style={{ maxWidth: '850px' }}>
        
        {/* Header Section */}
        <div className="p-4 bg-dark-blue text-white">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-2">
                <div className="bg-electricity p-2 rounded-circle">
                   <HiOutlineBolt className="text-dark fs-3" />
                </div>
                <div>
                  <h4 className="fw-bold mb-0">TopAirView</h4>
                  <p className="small mb-0 opacity-75">Utility Services - Electricity Bill</p>
                </div>
              </div>
            </div>
            <div className="col-md-6 text-md-end mt-3 mt-md-0">
              <h5 className="mb-0 text-uppercase fw-bold">Payment Receipt</h5>
              <p className="small mb-0 opacity-75">Ref: {booking.booking_reference}</p>
            </div>
          </div>
        </div>

        {/* Status Bar */}
        <div className="px-4 py-2 bg-light border-bottom d-flex justify-content-between align-items-center">
          <span className="small text-muted">Transaction Date: {new Date(booking.created_at).toLocaleString()}</span>
          <span className={`badge ${booking.status === 'SUCCESS' ? 'bg-success' : 'bg-danger'} px-3`}>
            {booking.status}
          </span>
        </div>

        <div className="p-4">
          <div className="row g-4">
            {/* Left Column: Biller & Consumer Info */}
            <div className="col-md-7">
              <section className="mb-4">
                <h6 className="fw-bold text-primary mb-3">
                  <BsReceipt className="me-2" /> Connection Details
                </h6>
                <div className="bg-light rounded p-3 border">
                  <div className="row mb-2">
                    <div className="col-5 text-muted small">Biller ID</div>
                    <div className="col-7 fw-bold">{request?.biller_id}</div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-5 text-muted small">Consumer Number</div>
                    <div className="col-7 fw-bold text-primary fs-5">{consumerNo || 'N/A'}</div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-5 text-muted small">Mobile Number</div>
                    <div className="col-7 fw-bold">{request?.mobile_number}</div>
                  </div>
                </div>
              </section>

              <section>
                <h6 className="fw-bold text-primary mb-3">
                  <HiOutlineInformationCircle className="me-2" /> Transaction Info
                </h6>
                <div className="p-3 border rounded">
                   <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">UTR / Operator Ref</span>
                      <span className="fw-bold">{response?.utr || 'Pending'}</span>
                   </div>
                   <div className="d-flex justify-content-between">
                      <span className="text-muted small">Reference ID</span>
                      <span className="fw-bold small">{request?.reference_id}</span>
                   </div>
                </div>
              </section>
            </div>

            {/* Right Column: Payment Summary */}
            <div className="col-md-5">
              <div className="card border-primary h-100">
                <div className="card-header bg-primary text-white text-center py-3">
                   <p className="small mb-0 opacity-75">Paid Amount</p>
                   <h2 className="fw-bold mb-0">₹{booking.amount}</h2>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Status</span>
                    <span className="text-success fw-bold">{response?.message?.toUpperCase()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Currency</span>
                    <span>{booking.currency}</span>
                  </div>
                  <div className="dashed-line"></div>
                  <div className="text-center">
                    <HiOutlineShieldCheck className="text-success fs-1 mb-2" />
                    <p className="small text-muted mb-0">Securely processed by Bharat Bill Payment System (BBPS)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-light rounded border border-dashed text-center">
            <small className="text-muted">
              This is a computer-generated receipt and does not require a physical signature. 
              The payment will reflect in your biller account within 2-3 working days.
            </small>
          </div>
        </div>

        {/* Contact Footer */}
        <div className="p-4 border-top">
          <div className="row text-center text-md-start">
            <div className="col-md-4 mb-3 mb-md-0">
               <p className="small fw-bold mb-1 text-primary">Support</p>
               <p className="small mb-0"><HiOutlineEnvelope /> a2zyatra2012@gmail.com</p>
            </div>
            <div className="col-md-4 mb-3 mb-md-0">
               <p className="small fw-bold mb-1 text-primary">Location</p>
               <p className="small mb-0"><HiOutlineBuildingOffice /> Haridwar, UK (249401)</p>
            </div>
            <div className="col-md-4 text-md-end">
               <p className="fw-bold mb-0">TopAirView Solutions</p>
               <p className="text-muted" style={{fontSize: '10px'}}>Empowering Digital Payments</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectricityInvoice;